/**
 * Files feature (Preact port): a two-pane page. The left pane shows the
 * workspace filesystem as an expandable tree; clicking a file previews it in
 * the right pane. Material the browser cannot render shows a placeholder.
 */
import { useState, useEffect } from 'preact/hooks';
import { getFSTree, getFSText, fsContentURL } from '../lib/api.js';
import { Icon } from '../components/Icons.jsx';

const formatSize = (bytes) => {
  const n = bytes || 0;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MiB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KiB`;
  return `${n} B`;
};

// previewKind maps a content type to the kind of in-browser preview we can
// render, or "none" if the material cannot be previewed.
function previewKind(ct = '') {
  if (ct.startsWith('image/')) return 'image';
  if (ct.startsWith('text/')) return 'text';
  if (ct === 'application/json' || ct === 'application/javascript') return 'text';
  if (ct.startsWith('audio/')) return 'audio';
  if (ct.startsWith('video/')) return 'video';
  if (ct === 'application/pdf') return 'pdf';
  return 'none';
}

export function FSPage() {
  const [tree, setTree] = useState(null);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(null); // fsNode (file or dir)

  useEffect(() => {
    let cancelled = false;
    getFSTree()
      .then((t) => {
        if (!cancelled) setTree(t);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex items-center gap-4 px-4 py-3 border-b border-base-300 bg-base-100">
        <h1 className="text-lg font-semibold text-base-content truncate">Files</h1>
      </div>

      <div className="flex-1 flex min-h-0">
        {/* Filesystem tree sidebar */}
        <aside className="w-72 shrink-0 border-r border-base-300 overflow-y-auto p-2 bg-base-100">
          {error ? (
            <p className="text-base-content/60 text-sm p-2">Failed to load files.</p>
          ) : tree === null ? (
            <p className="text-base-content/60 text-sm p-2">Loading…</p>
          ) : (
            <FSTree
              nodes={tree.children || []}
              selectedPath={selected && selected.path}
              onSelect={setSelected}
            />
          )}
        </aside>

        {/* Preview pane */}
        <section className="flex-1 min-w-0 overflow-y-auto px-4 py-4">
          <PreviewPane node={selected} />
        </section>
      </div>
    </div>
  );
}

// FSTree renders a list of fs nodes recursively.
function FSTree({ nodes = [], selectedPath, onSelect, depth = 0 }) {
  if (!nodes.length) {
    return <p className="text-base-content/50 text-sm px-2">Empty</p>;
  }
  return (
    <div className="flex flex-col">
      {nodes.map((n) => (
        <FSTreeNode
          key={n.path || n.name}
          node={n}
          depth={depth}
          selectedPath={selectedPath}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

function FSTreeNode({ node, depth, selectedPath, onSelect }) {
  const [open, setOpen] = useState(node.is_dir && depth === 0);
  const pad = { paddingLeft: `${depth * 14 + 4}px` };

  if (node.is_dir) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center gap-1 rounded-lg px-1 py-1 text-sm text-base-content/70 hover:bg-base-200 transition-colors"
          style={pad}
        >
          <Icon
            name={open ? 'chevron-down' : 'chevron-right'}
            className="w-3.5 h-3.5 shrink-0 text-base-content/40"
          />
          <Icon name="folder" className="w-4 h-4 shrink-0 text-base-content/50" />
          <span className="truncate">{node.name}</span>
        </button>
        {open && (
          <FSTree
            nodes={node.children || []}
            depth={depth + 1}
            selectedPath={selectedPath}
            onSelect={onSelect}
          />
        )}
      </div>
    );
  }

  const active = selectedPath === node.path;
  return (
    <button
      type="button"
      onClick={() => onSelect(node)}
      className={`w-full flex items-center gap-1 rounded-lg px-1 py-1 text-sm transition-colors ${
        active ? 'bg-primary/15 text-base-content' : 'text-base-content/70 hover:bg-base-200'
      }`}
      style={pad}
    >
      <Icon name="document-text" className="w-4 h-4 shrink-0 text-base-content/50" />
      <span className="truncate">{node.name}</span>
    </button>
  );
}

// PreviewPane shows a preview for the selected node, or guidance when nothing
// is selected. Unpreviewable files show a placeholder with a download link.
function PreviewPane({ node }) {
  if (!node) {
    return (
      <EmptyState>
        <Icon name="folder" className="w-10 h-10 text-base-content/30" />
        <p className="text-base-content/60">Select a file to preview it.</p>
      </EmptyState>
    );
  }

  if (node.is_dir) {
    return (
      <EmptyState>
        <Icon name="folder" className="w-10 h-10 text-base-content/30" />
        <p className="text-base-content/60 font-medium">{node.name}</p>
        <p className="text-base-content/40 text-sm">{(node.children || []).length} items</p>
      </EmptyState>
    );
  }

  const url = fsContentURL(node.path);
  const kind = previewKind(node.content_type);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Icon name="document-text" className="w-6 h-6 shrink-0 text-base-content/50" />
        <div className="min-w-0">
          <h2 className="font-semibold text-base-content truncate">{node.name}</h2>
          <p className="text-sm text-base-content/50 truncate">{node.path}</p>
        </div>
        <a href={url} target="_blank" rel="noopener" className="btn btn-ghost btn-sm ml-auto">
          ↗ Open
        </a>
      </div>

      <div className="text-sm text-base-content/60 grid grid-cols-2 gap-x-6 gap-y-1 max-w-md">
        <Meta label="Size" value={formatSize(node.size)} />
        <Meta label="Type" value={node.content_type || 'unknown'} />
        <Meta label="Permissions" value={node.permissions || '---'} />
        <Meta label="Modified" value={node.mod_time ? new Date(node.mod_time * 1000).toLocaleString() : '—'} />
      </div>

      <div className="border-t border-base-300 pt-4">
        {kind === 'image' && (
          <img src={url} alt={node.name} className="max-w-full max-h-[70vh] rounded-lg object-contain" />
        )}
        {kind === 'text' && <TextPreview path={node.path} />}
        {kind === 'audio' && <audio controls className="w-full" src={url} />}
        {kind === 'video' && <video controls className="max-w-full rounded-lg" src={url} />}
        {kind === 'pdf' && (
          <iframe src={url} title={node.name} className="w-full h-[70vh] rounded-lg border border-base-300" />
        )}
        {kind === 'none' && (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <Icon name="document-text" className="w-12 h-12 shrink-0 text-base-content/30" />
            <div>
              <p className="text-base-content/70 font-medium">No preview available</p>
              <p className="text-base-content/40 text-sm">This file type cannot be previewed in the browser.</p>
            </div>
            <a href={url} target="_blank" rel="noopener" className="btn btn-outline btn-sm">
              Download
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <span className="text-base-content/40">{label}: </span>
      <span className="text-base-content/80">{value}</span>
    </div>
  );
}

function TextPreview({ path }) {
  const [text, setText] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setText(null);
    setErr(false);
    getFSText(path)
      .then((t) => {
        if (!cancelled) setText(t);
      })
      .catch(() => {
        if (!cancelled) setErr(true);
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  if (err) return <p className="text-base-content/60 text-sm">Failed to load text.</p>;
  if (text === null) return <p className="text-base-content/60 text-sm">Loading…</p>;
  return (
    <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed bg-base-200/60 rounded-lg p-3 max-h-[70vh] overflow-auto">
      {text}
    </pre>
  );
}

function EmptyState({ children }) {
  return (
    <div className="h-full grid place-items-center">
      <div className="flex flex-col items-center gap-2 text-center">{children}</div>
    </div>
  );
}
